import { useState } from 'react';
import { LocalizedLink as Link } from './LocalizedLink';
import { useTranslation } from 'react-i18next';
import { Cookie } from 'lucide-react';
import { getStoredConsent, setConsent, type ConsentValue } from '../lib/consent';

/**
 * Banner de consentimento de cookies (LGPD/GDPR). Aparece só enquanto o
 * usuário não decidiu; a escolha alimenta o Google Consent Mode v2
 * (GA + AdSense) via lib/consent.ts. Mobile-first.
 */
export function CookieConsentBanner() {
  const { t } = useTranslation('common');
  // Estado inicial = null significa "ainda não decidiu" → mostra o banner.
  const [visible, setVisible] = useState(() => getStoredConsent() === null);

  if (!visible) return null;

  const decide = (value: ConsentValue) => {
    setConsent(value);
    setVisible(false);
  };

  return (
    <div
      role="dialog"
      aria-label={t('cookie.title', 'Privacidade e cookies')}
      className="fixed inset-x-0 bottom-0 z-[60] p-3 sm:p-4"
    >
      <div className="mx-auto max-w-3xl rounded-2xl border border-gray-700 bg-gray-900/95 backdrop-blur p-4 shadow-2xl sm:flex sm:items-center sm:gap-4">
        <div className="flex items-start gap-3">
          <Cookie className="w-6 h-6 text-primary-400 flex-shrink-0 mt-0.5" aria-hidden="true" />
          <div>
            <p className="text-sm font-semibold text-white">
              {t('cookie.title', 'Privacidade e cookies')}
            </p>
            <p className="mt-1 text-xs text-gray-300 leading-relaxed">
              {t(
                'cookie.message',
                'Usamos cookies para medir o tráfego do site e, futuramente, exibir anúncios. Cookies essenciais ao funcionamento permanecem sempre ativos.',
              )}{' '}
              <Link to="/privacidade" className="text-primary-300 underline hover:text-primary-200">
                {t('cookie.policy', 'Política de Privacidade')}
              </Link>
            </p>
          </div>
        </div>
        <div className="mt-3 flex gap-2 sm:mt-0 sm:flex-shrink-0">
          <button
            type="button"
            onClick={() => decide('denied')}
            className="flex-1 sm:flex-none px-4 py-2 text-sm font-semibold text-gray-200 bg-gray-700 rounded-xl hover:bg-gray-600 transition-colors"
          >
            {t('cookie.reject', 'Recusar')}
          </button>
          <button
            type="button"
            onClick={() => decide('granted')}
            className="flex-1 sm:flex-none px-4 py-2 text-sm font-semibold text-white bg-primary-600 rounded-xl hover:bg-primary-700 transition-colors"
          >
            {t('cookie.accept', 'Aceitar')}
          </button>
        </div>
      </div>
    </div>
  );
}

export default CookieConsentBanner;
